import { useTranslation } from 'next-i18next'
import React from 'react'

import s from './landing.module.css'
import { Persona } from './persona'

const BELT_SWATCHES: Array<{
  key: string
  swatch: React.CSSProperties
  width: string
  pts: number
}> = [
  { key: 'beltWhite', swatch: { background: '#f2f0ec' }, width: '20%', pts: 5 },
  { key: 'beltBlue', swatch: { background: '#2456d6' }, width: '32%', pts: 8 },
  {
    key: 'beltPurple',
    swatch: { background: '#7b3ff2' },
    width: '48%',
    pts: 12,
  },
  {
    key: 'beltBrown',
    swatch: { background: '#8a5a34' },
    width: '72%',
    pts: 18,
  },
  {
    key: 'beltBlack',
    swatch: { background: '#3a3a3e', border: '1px solid #666' },
    width: '100%',
    pts: 25,
  },
]

/** Card 360: peso do voto por faixa + card do atleta com radar octogonal. */
export function Card360({ persona }: { persona: Persona }) {
  const { t } = useTranslation('landing')

  return (
    <section
      className={`bg-section-black ${s.sec}`}
      id="card360"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      <div
        className={s.ghost}
        style={{
          fontSize: 'clamp(160px,24vw,420px)',
          insetInlineEnd: '-4%',
          top: -30,
        }}
        aria-hidden="true"
      >
        360
      </div>
      <p className={`${s.kick} text-brand`} style={{ position: 'relative' }}>
        <span className={s.kbar} />
        {t('card360.kicker')}
      </p>
      <h2
        className={`${s.d} ${s.h2} text-white`}
        style={{ margin: '22px 0 0', position: 'relative' }}
      >
        {t('card360.title1')}
        <br />
        <span className={s.outW}>{t('card360.title2')}</span>
      </h2>

      <div
        style={{
          display: 'flex',
          gap: 24,
          flexWrap: 'wrap',
          marginTop: 16,
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <p className={s.lead} style={{ color: 'rgba(255,255,255,.85)' }}>
            {t('card360.lead')}
          </p>
          <div
            className={`${s.cond} text-white`}
            style={{
              fontWeight: 700,
              fontSize: 14,
              letterSpacing: '.2em',
              margin: '40px 0 16px',
            }}
          >
            {t('card360.voteWeight')}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxWidth: 440,
            }}
          >
            {BELT_SWATCHES.map((belt) => (
              <div className={`${s.beltrow} text-white`} key={belt.key}>
                <span className={s.bsw} style={belt.swatch} />
                <span className={s.bname}>{t(`card360.${belt.key}`)}</span>
                <div className={s.bbar} style={{ width: belt.width }} />
                <span className={s.bpts}>{belt.pts}</span>
              </div>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 36,
            }}
          >
            <a className={`${s.btn} ${s.btnR}`} href="#baixar">
              {t('card360.ctaCreate')}
            </a>
            <a className={`${s.btn} ${s.btnO}`} href="#lutas">
              {t('card360.ctaCompare')}
            </a>
          </div>
        </div>

        <AthleteCard persona={persona} />
      </div>
    </section>
  )
}

/** Card branco do atleta com radar octogonal de 8 habilidades. */
function AthleteCard({ persona }: { persona: Persona }) {
  const { t } = useTranslation('landing')

  const labels: Array<{
    x: number
    y: number
    anchor: 'start' | 'middle' | 'end'
    key: string
  }> = [
    { x: 100, y: 14, anchor: 'middle', key: 'guard' },
    { x: 163, y: 40, anchor: 'middle', key: 'pass' },
    { x: 196, y: 103, anchor: 'end', key: 'sweep' },
    { x: 164, y: 170, anchor: 'middle', key: 'takedown' },
    { x: 100, y: 194, anchor: 'middle', key: 'submission' },
    { x: 36, y: 170, anchor: 'middle', key: 'subDefense' },
    { x: 4, y: 103, anchor: 'start', key: 'recovery' },
    { x: 34, y: 40, anchor: 'middle', key: 'posDefense' },
  ]

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 24,
        padding: 26,
        width: 'min(360px, 88vw)',
        boxShadow: '0 40px 80px rgba(0,0,0,.5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          className={s.ava}
          style={{ width: 56, height: 56 }}
          aria-hidden="true"
        >
          {persona.init}
        </div>
        <div style={{ flex: 1 }}>
          <div className={`${s.d} text-ink`} style={{ fontSize: 24 }}>
            {persona.name}
          </div>
          <div style={{ fontSize: 13, color: '#7b7b7b' }}>
            {t('card360.cardMeta')}
          </div>
        </div>
        <div
          className={`${s.cond} text-brand`}
          style={{ fontWeight: 700, fontSize: 13, letterSpacing: '.12em' }}
        >
          214 OSS
        </div>
      </div>

      <svg
        viewBox="0 0 200 200"
        style={{ width: '100%', marginTop: 10 }}
        aria-label={t('card360.radarAria')}
        role="img"
      >
        <polygon
          points="100,20 156.6,43.4 180,100 156.6,156.6 100,180 43.4,156.6 20,100 43.4,43.4"
          fill="none"
          stroke="#e6e3dd"
          strokeWidth="1.5"
        />
        <polygon
          points="100,47 137.5,62.5 153,100 137.5,137.5 100,153 62.5,137.5 47,100 62.5,62.5"
          fill="none"
          stroke="#e6e3dd"
          strokeWidth="1.5"
        />
        <polygon
          points="100,73 119,81 127,100 119,119 100,127 81,119 73,100 81,81"
          fill="none"
          stroke="#e6e3dd"
          strokeWidth="1.5"
        />
        <polygon
          points="100,28 142.4,57.6 176,100 131.1,131.1 100,168 63.2,136.8 36,100 59,59"
          fill="rgba(236,27,46,.22)"
          stroke="#EC1B2E"
          strokeWidth="2.5"
        />
        {labels.map((label) => (
          <text
            key={label.key}
            x={label.x}
            y={label.y}
            textAnchor={label.anchor}
            fontSize="9"
            fontWeight="600"
            fill="#8b8b8b"
          >
            {t(`card360.skills.${label.key}`)}
          </text>
        ))}
      </svg>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 8,
          borderTop: '2px solid #eceae6',
          paddingTop: 14,
        }}
      >
        <span
          className={s.cond}
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#8b8b8b',
            letterSpacing: '.14em',
          }}
        >
          {t('card360.evolution')}
        </span>
        <span
          className={`${s.cond} text-brand`}
          style={{ fontWeight: 700, fontSize: 13, letterSpacing: '.1em' }}
        >
          {t('card360.evolutionValue')}
        </span>
      </div>
    </div>
  )
}
